/** Enough to (re)create or (re)place a block. Times are UK wall-clock 'YYYY-MM-DD HH:mm'. */
export interface SlotSpec {
  fitterId: number;
  jobType: 'defect' | 'inspection';
  /** workshopDefectId or vehicleInspectionId, by jobType. */
  linkId: number;
  startAt: string;
  endAt: string;
}

export type HistoryOp =
  | { kind: 'create'; id: number; spec: SlotSpec }
  | { kind: 'update'; id: number; before: SlotSpec; after: SlotSpec }
  | { kind: 'remove'; id: number; spec: SlotSpec };

const LIMIT = 20;

/**
 * Undo/redo for the scheduler board (drops, moves, resizes, removals; completions are not undoable).
 * Undoing a removal re-creates the block with a new id, so `remapId` points every other step at it.
 */
export class ScheduleHistory {
  private undo: HistoryOp[] = [];
  private redo: HistoryOp[] = [];

  record(op: HistoryOp): void {
    this.undo.push(op);
    if (this.undo.length > LIMIT) {
      this.undo.shift();
    }
    this.redo = [];
  }

  /** The step to undo (moved onto the redo stack), or null. */
  takeUndo(): HistoryOp | null {
    const op = this.undo.pop() || null;
    if (op) {
      this.redo.push(op);
    }
    return op;
  }

  /** The step to redo (moved back onto the undo stack), or null. */
  takeRedo(): HistoryOp | null {
    const op = this.redo.pop() || null;
    if (op) {
      this.undo.push(op);
    }
    return op;
  }

  remapId(oldId: number, newId: number): void {
    for (const op of [...this.undo, ...this.redo]) {
      if (op.id === oldId) {
        op.id = newId;
      }
    }
  }

  /** Drops every step for a block - once it is completed, none of them may be replayed. */
  forget(id: number): void {
    this.undo = this.undo.filter((op) => op.id !== id);
    this.redo = this.redo.filter((op) => op.id !== id);
  }

  canUndo(): boolean {
    return this.undo.length > 0;
  }

  canRedo(): boolean {
    return this.redo.length > 0;
  }

  clear(): void {
    this.undo = [];
    this.redo = [];
  }
}
